import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useAuth } from '../context/AuthContext';
import { resumeSubscription } from '../services/subscription-service';

interface ResumeSubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onResume: () => void;
    onError: (message: string) => void;
}

const ResumeSubscriptionModal: React.FC<ResumeSubscriptionModalProps> = ({
                                                                             isOpen,
                                                                             onClose,
                                                                             onResume,
                                                                             onError,
                                                                         }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);

    const handleConfirmResume = async () => {
        if (!user?.id) {
            onError('There was a problem while attempting to resume. Please try logging in again.');
            onClose();
            return;
        }

        setLoading(true);
        try {
            const response = await resumeSubscription(user.id);
            if (response.status === 200) {
                onResume();
            } else {
                onError('Unable to resume at this time. Please try again later or contact support if the issue persists.');
            }
        } catch {
            onError('Unable to resume at this time. Please try again later or contact support if the issue persists.');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
                <Dialog.Title className="text-xl font-semibold mb-4 text-crumbblue">
                    Resume Subscription
                </Dialog.Title>

                <p className="mb-2">
                    Do you want to resume your subscription?
                </p>
                <p className="mb-4 text-sm text-gray-600">
                    If you resume now, your plan will continue without interruption and your next payment
                    will be charged at the end of your current billing cycle.
                </p>

                <div className="flex justify-end space-x-3">
                    <button
                        data-testid="close-btn"
                        onClick={onClose}
                        className="px-4 py-2 rounded border"
                        disabled={loading}
                    >
                        Keep cancellation
                    </button>
                    <button
                        data-testid="confirm-resume-btn"
                        onClick={handleConfirmResume}
                        className="px-4 py-2 rounded bg-crumbblue text-white disabled:opacity-50"
                        disabled={loading}
                    >
                        {loading ? 'Resuming...' : 'Yes, resume'}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default ResumeSubscriptionModal;